import Dce from "dce-engine/lib/decorators/Dce";
import DceComponent from "dce-engine/lib/engine/DceComponent";
import DceHTMLElement from "dce-engine/lib/types/DceHTMLElement";
import {Editor} from "@tiptap/core";
import {EditorState, Transaction} from 'prosemirror-state'
import Toolbar from "./toolbar/Toolbar";
import {ToolbarItemFactory} from "./toolbar/ToolbarItemFactory";
import {Image} from "../extension-image/image/image";
import {ImageItem} from "./toolbar/images/ImageItem";
import {ImageFloatItem} from "./toolbar/images/ImageFloatItem";
import {BlockFormatsItem, BlockFormatsOptions} from "./toolbar/BlockFormats";
import {TableItem} from "./toolbar/table/TableItem";
import {Youtube} from "@tiptap/extension-youtube";
import YouTubeButton from "./toolbar/buttons/YouTubeButton";
import TipTapEditorLoader from "./TipTapEditorLoader";
import {BlockClass} from "../extension-block-class/block-class";
import {basicToolbar, buildToolbar, fullToolbar} from "./toolbar/ToolbarPredefined";
import AiGeneratorButton from "./toolbar/buttons/AiGeneratorButton";
import {AiGeneratorNode} from "../extension-ai-generator/ai-generator/ai-generator";

interface TipTapEditorProps {
    height: number,
    maxWidth: number,
    imageDialogUrl?: string,
    basicToolbar: boolean,

    toolbar?: {
        blockFormat: BlockFormatsOptions
    },

    toolbarItems? : string[][]
}

interface TransactionEvent {
    editor: Editor,
    transaction: Transaction
}

@Dce("TipTapEditor")
export default class TipTapEditor extends DceComponent<TipTapEditorProps> {

    private editor: Editor;

    private content: HTMLElement;
    private toolbar: HTMLElement;

    private textarea: HTMLInputElement;

    private tc: Toolbar;

    constructor(elem: DceHTMLElement, props: TipTapEditorProps) {
        super(elem, props);

        this.content = document.createElement('div');
        this.content.setAttribute('data-dce-ignore', "");
        this.content.classList.add('tiptap-editor-content');

        this.content.style.minHeight = this.props.height + "px";

        if (this.elem.tagName.toLowerCase() == 'input') {

            this.textarea = this.elem as unknown as HTMLInputElement;

            if (this.elem.parentElement.classList.contains("tiptap-editor")) {
                this.elem.parentElement.append(this.content);

                if(this.props.maxWidth > 0){
                    this.elem.parentElement.style.maxWidth = this.props.maxWidth + "px";
                }

            } else {
                let e = document.createElement('div');
                e.classList.add("tiptap-editor");
                if(this.props.maxWidth > 0){
                    e.style.maxWidth = this.props.maxWidth + "px";
                }

                this.elem.after(e);
                e.append(this.content);
            }

        } else {
            this.elem.append(this.content);
            this.textarea = this.elem.querySelector('textarea, input');
        }

        TipTapEditorLoader.load().then(value => {

            let startContent = "";
            if (this.textarea && this.textarea.value) {
                startContent = this.textarea.value;
            }

            let LinkExt = value.Link.configure({
                openOnClick: false,
                HTMLAttributes: {
                    "target": "",
                    "rel": ""
                }
            });

            this.editor = new value.Editor({
                element: this.content,
                extensions: [
                    value.StarterKit,
                    value.Underline,
                    value.TextAlign.configure({
                        types: ['heading', 'paragraph'],
                    }),
                    Image,
                    value.Table.configure({
                        resizable: true,
                    }),
                    value.TableRow,
                    value.TableCell,
                    value.TableHeader,
                    LinkExt,
                    Youtube.configure({
                        inline: false,
                        controls: true,
                        modestBranding: true
                    }),
                    BlockClass.configure(),
                    AiGeneratorNode,
                ],
                content: startContent,
            });

            this.initEditorUi();

            if (null != this.textarea) {
                this.editor.on('update', () => {
                    this.updateTextarea();
                })
            }

        });


    }

    private updateTextarea() {
        this.textarea.value = this.editor.getHTML();
    }
    private initEditorUi() {

        let factory = new ToolbarItemFactory([
            ['block-formats', () => {
                let blockFormats: BlockFormatsOptions = {};
                if (this.props.toolbar && this.props.toolbar.blockFormat) {
                    blockFormats = this.props.toolbar.blockFormat;
                }
                return new BlockFormatsItem(blockFormats)
            }],

            ['image', () => {
                return new ImageItem({
                    dialogUrl: this.props.imageDialogUrl ? this.props.imageDialogUrl : "/file_browser"
                });
            }],
            ['image-float', () => new ImageFloatItem()],

            ['youtube', () => new YouTubeButton()],

            ['table', () => new TableItem()],

            ['ai-generator', () => new AiGeneratorButton()]
        ]);

        this.tc = new Toolbar();

        if(this.props.toolbarItems){
            buildToolbar(this.props.toolbarItems, this.tc, factory)
        }else{
            if(this.props.basicToolbar){
                basicToolbar(this.tc, factory);
            }else{
                fullToolbar(this.tc, factory);
            }
        }

        this.tc.mount(this.editor);

        this.toolbar = this.tc.generateElement();
        this.content.before(this.toolbar);

        this.editor.on('transaction', (e: TransactionEvent) => {
            this.tc.consumeTransaction(e.editor, e.transaction);
        });
    }

    defaultProps(): Partial<TipTapEditorProps> {
        return {
            height: 200,
            maxWidth: 920,
            basicToolbar: false
        }
    }
}
